import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Shield, 
  Plus, 
  User,
  MapPin,
  Calendar,
  DollarSign,
  CheckCircle,
  Clock,
  AlertTriangle,
  FileText,
  Search,
  Trash2,
  Edit,
  BookOpen
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATO_BADGE = {
  DA_PAGARE: { color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  PAGATA: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  CONTESTATA: { color: 'bg-yellow-100 text-yellow-800', icon: Clock }
};

const TIPI_NOTA = [
  { value: 'AMMONIZIONE', label: 'Ammonizione', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'SEGNALAZIONE', label: 'Segnalazione', color: 'bg-orange-100 text-orange-800' },
  { value: 'NOTA_POSITIVA', label: 'Nota Positiva', color: 'bg-green-100 text-green-800' },
  { value: 'RIABILITAZIONE', label: 'Riabilitazione', color: 'bg-blue-100 text-blue-800' }
];

const Multe = () => {
  const { user, hasPermesso, isSindacatoAdmin } = useAuth();
  const [multe, setMulte] = useState([]);
  const [mieMulte, setMieMulte] = useState([]);
  const [users, setUsers] = useState([]);
  const [fedina, setFedina] = useState(null);
  const [searchFedinaId, setSearchFedinaId] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fedinaDialogOpen, setFedinaDialogOpen] = useState(false);
  const [notaDialogOpen, setNotaDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedMulta, setSelectedMulta] = useState(null);
  const [form, setForm] = useState({
    cittadino_id: '',
    motivo: '',
    importo: '',
    luogo: '',
    data_infrazione: new Date().toISOString().split('T')[0],
    note: ''
  });
  const [notaForm, setNotaForm] = useState({
    cittadino_id: '',
    tipo: 'AMMONIZIONE',
    descrizione: '',
    gravita: 1
  });
  const [editForm, setEditForm] = useState({
    motivo: '',
    importo: '',
    note: ''
  });

  const canInserisciMulte = () => isSindacatoAdmin() || hasPermesso('inserisci_multe');
  const canGestisciPagamenti = () => isSindacatoAdmin() || hasPermesso('gestisci_pagamenti_multe');
  const canVisualizzaFedina = () => isSindacatoAdmin() || hasPermesso('visualizza_fedina_penale') || hasPermesso('inserisci_multe');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const promises = [axios.get(`${API_URL}/multe/mie`)];
      
      if (canInserisciMulte() || canGestisciPagamenti()) {
        promises.push(axios.get(`${API_URL}/multe`));
        promises.push(axios.get(`${API_URL}/users`));
      }
      
      const results = await Promise.all(promises);
      setMieMulte(results[0].data);
      
      if (results[1]) setMulte(results[1].data);
      if (results[2]) setUsers(results[2].data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMulta = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/multe`, {
        ...form,
        importo: parseFloat(form.importo)
      });
      toast.success('Multa registrata');
      setDialogOpen(false);
      setForm({
        cittadino_id: '',
        motivo: '',
        importo: '',
        luogo: '',
        data_infrazione: new Date().toISOString().split('T')[0],
        note: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella registrazione');
    }
  };

  const handlePagaMulta = async (multaId) => {
    try {
      await axios.patch(`${API_URL}/multe/${multaId}/paga`);
      toast.success('Multa pagata');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel pagamento');
    }
  };

  const handleDeleteMulta = async (multaId) => {
    if (!window.confirm('Eliminare questa multa? L\'azione aggiornerà automaticamente la fedina penale.')) return;
    try {
      await axios.delete(`${API_URL}/multe/${multaId}`);
      toast.success('Multa eliminata');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleEditMulta = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams();
      if (editForm.motivo) params.append('motivo', editForm.motivo);
      if (editForm.importo) params.append('importo', editForm.importo);
      if (editForm.note !== undefined) params.append('note', editForm.note);
      
      await axios.patch(`${API_URL}/multe/${selectedMulta.id}?${params.toString()}`);
      toast.success('Multa aggiornata');
      setEditDialogOpen(false);
      setSelectedMulta(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella modifica');
    }
  };

  const handleSearchFedina = async () => {
    if (!searchFedinaId) {
      toast.error('Seleziona un cittadino');
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/fedina-penale/${searchFedinaId}/completa`);
      setFedina(res.data);
      setFedinaDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel recupero fedina');
    }
  };

  const handleAddNota = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/fedina-penale/${notaForm.cittadino_id}/nota`, notaForm);
      toast.success('Nota aggiunta alla fedina penale');
      setNotaDialogOpen(false);
      setNotaForm({
        cittadino_id: '',
        tipo: 'AMMONIZIONE',
        descrizione: '',
        gravita: 1
      });
      // Ricarica fedina se aperta
      if (fedina && fedina.cittadino_id === notaForm.cittadino_id) {
        handleSearchFedina();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta della nota');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getStatoBadge = (stato) => {
    const config = STATO_BADGE[stato] || { color: 'bg-slate-100 text-slate-800', icon: Clock };
    const Icon = config.icon;
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {stato.replace('_', ' ')}
      </Badge>
    );
  };

  const renderMultaCard = (multa, showActions = false) => (
    <Card key={multa.id} className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <User className="w-4 h-4" />
              {multa.cittadino_nome}
            </CardTitle>
            <CardDescription>{multa.motivo}</CardDescription>
          </div>
          <div className="text-right">
            {getStatoBadge(multa.stato)}
            <p className="text-lg font-bold text-slate-900 mt-1">{formatCurrency(multa.importo)}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-2 text-sm text-slate-600 mb-3">
          <div className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {multa.luogo}
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {new Date(multa.data_infrazione).toLocaleDateString('it-IT')}
          </div>
        </div>
        
        {multa.note && (
          <p className="text-sm text-slate-500 mb-3">{multa.note}</p>
        )}
        
        <div className="text-xs text-slate-400">
          Agente: {multa.agente_nome}
          {multa.stato === 'PAGATA' && multa.data_pagamento && (
            <span className="block text-green-600">
              Pagata il {new Date(multa.data_pagamento).toLocaleDateString('it-IT')} presso {multa.pagato_presso}
            </span>
          )}
        </div>

        {showActions && (
          <div className="flex gap-2 mt-3 pt-3 border-t">
            {multa.stato === 'DA_PAGARE' && canGestisciPagamenti() && (
              <Button size="sm" onClick={() => handlePagaMulta(multa.id)}>
                <CheckCircle className="w-4 h-4 mr-1" />
                Paga
              </Button>
            )}
            {canInserisciMulte() && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setSelectedMulta(multa);
                  setEditForm({
                    motivo: multa.motivo,
                    importo: multa.importo,
                    note: multa.note || ''
                  });
                  setEditDialogOpen(true);
                }}
              >
                <Edit className="w-4 h-4 mr-1" />
                Modifica
              </Button>
            )}
            {isSindacatoAdmin() && (
              <Button
                size="sm"
                variant="ghost"
                className="text-red-600"
                onClick={() => handleDeleteMulta(multa.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="multe-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestione Multe e Fedina Penale</h1>
          <p className="text-slate-600">Forze dell'Ordine e Pagamenti Postali</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canInserisciMulte() && (
            <>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="nuova-multa-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuova Multa
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Registra Multa</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateMulta} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Cittadino</Label>
                      <select
                        className="w-full border rounded-md p-2"
                        value={form.cittadino_id}
                        onChange={(e) => setForm({...form, cittadino_id: e.target.value})}
                        required
                      >
                        <option value="">Seleziona cittadino...</option>
                        {users.map(u => (
                          <option key={u.id} value={u.id}>{u.nome} {u.cognome}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Motivo dell'infrazione</Label>
                      <Textarea
                        value={form.motivo}
                        onChange={(e) => setForm({...form, motivo: e.target.value})}
                        placeholder="Descrizione dell'infrazione..."
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Importo (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={form.importo}
                          onChange={(e) => setForm({...form, importo: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Data Infrazione</Label>
                        <Input
                          type="date"
                          value={form.data_infrazione}
                          onChange={(e) => setForm({...form, data_infrazione: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Luogo</Label>
                      <Input
                        value={form.luogo}
                        onChange={(e) => setForm({...form, luogo: e.target.value})}
                        placeholder="es. Via Roma, 15"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Note (opzionale)</Label>
                      <Textarea
                        value={form.note}
                        onChange={(e) => setForm({...form, note: e.target.value})}
                        placeholder="Note aggiuntive..."
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                      <Button type="submit">Registra Multa</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={notaDialogOpen} onOpenChange={setNotaDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="aggiungi-nota-btn">
                    <BookOpen className="w-4 h-4 mr-2" />
                    Aggiungi Nota Fedina
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Aggiungi Nota alla Fedina Penale</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddNota} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Cittadino</Label>
                      <select
                        className="w-full border rounded-md p-2"
                        value={notaForm.cittadino_id}
                        onChange={(e) => setNotaForm({...notaForm, cittadino_id: e.target.value})}
                        required
                      >
                        <option value="">Seleziona cittadino...</option>
                        {users.map(u => (
                          <option key={u.id} value={u.id}>{u.nome} {u.cognome}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Tipo Nota</Label>
                      <select
                        className="w-full border rounded-md p-2"
                        value={notaForm.tipo}
                        onChange={(e) => setNotaForm({...notaForm, tipo: e.target.value})}
                      >
                        {TIPI_NOTA.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Gravità (1-5)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="5"
                        value={notaForm.gravita}
                        onChange={(e) => setNotaForm({...notaForm, gravita: parseInt(e.target.value)})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descrizione</Label>
                      <Textarea
                        value={notaForm.descrizione}
                        onChange={(e) => setNotaForm({...notaForm, descrizione: e.target.value})}
                        placeholder="Descrizione della nota..."
                        required
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setNotaDialogOpen(false)}>Annulla</Button>
                      <Button type="submit">Aggiungi Nota</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}

          {canVisualizzaFedina() && (
            <div className="flex gap-2">
              <select
                className="border rounded-md p-2 text-sm"
                value={searchFedinaId}
                onChange={(e) => setSearchFedinaId(e.target.value)}
              >
                <option value="">Cerca fedina...</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.nome} {u.cognome}</option>
                ))}
              </select>
              <Button variant="outline" onClick={handleSearchFedina}>
                <Search className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="mie" className="w-full">
        <TabsList>
          <TabsTrigger value="mie" className="gap-2">
            <User className="w-4 h-4" />
            Le Mie Multe
          </TabsTrigger>
          {(canInserisciMulte() || canGestisciPagamenti()) && (
            <TabsTrigger value="tutte" className="gap-2">
              <Shield className="w-4 h-4" />
              Tutte le Multe
            </TabsTrigger>
          )}
          {canGestisciPagamenti() && (
            <TabsTrigger value="da_pagare" className="gap-2">
              <DollarSign className="w-4 h-4" />
              Da Pagare ({multe.filter(m => m.stato === 'DA_PAGARE').length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Le Mie Multe */}
        <TabsContent value="mie" className="mt-6">
          {mieMulte.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle className="w-12 h-12 text-green-300 mb-4" />
                <p className="text-slate-500">Nessuna multa registrata</p>
                <p className="text-sm text-slate-400">Complimenti, la tua fedina è pulita!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {mieMulte.map(multa => renderMultaCard(multa))}
            </div>
          )}
        </TabsContent>

        {/* Tutte le Multe */}
        {(canInserisciMulte() || canGestisciPagamenti()) && (
          <TabsContent value="tutte" className="mt-6">
            {multe.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Shield className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-slate-500">Nessuna multa registrata</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {multe.map(multa => renderMultaCard(multa, true))}
              </div>
            )}
          </TabsContent>
        )}

        {/* Da Pagare */}
        {canGestisciPagamenti() && (
          <TabsContent value="da_pagare" className="mt-6">
            {multe.filter(m => m.stato === 'DA_PAGARE').length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <CheckCircle className="w-12 h-12 text-green-300 mb-4" />
                  <p className="text-slate-500">Nessuna multa da pagare</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {multe.filter(m => m.stato === 'DA_PAGARE').map(multa => renderMultaCard(multa, true))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Dialog Modifica Multa */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Multa</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditMulta} className="space-y-4">
            <div className="space-y-2">
              <Label>Motivo</Label>
              <Textarea
                value={editForm.motivo}
                onChange={(e) => setEditForm({...editForm, motivo: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Importo (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={editForm.importo}
                onChange={(e) => setEditForm({...editForm, importo: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={editForm.note}
                onChange={(e) => setEditForm({...editForm, note: e.target.value})}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Annulla</Button>
              <Button type="submit">Salva Modifiche</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Fedina Penale */}
      <Dialog open={fedinaDialogOpen} onOpenChange={setFedinaDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Fedina Penale Completa
            </DialogTitle>
          </DialogHeader>
          {fedina && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg">{fedina.cittadino_nome}</h3>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <p className="text-sm text-slate-500">Multe Totali</p>
                    <p className="text-2xl font-bold">{fedina.multe_totali}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Non Pagate</p>
                    <p className="text-2xl font-bold text-red-600">{fedina.multe_non_pagate}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Importo Totale</p>
                    <p className="text-lg font-medium">{formatCurrency(fedina.importo_totale_multe)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Da Pagare</p>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(fedina.importo_da_pagare)}</p>
                  </div>
                </div>
              </div>

              {/* Note */}
              {fedina.note && fedina.note.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">Note sulla Fedina ({fedina.note.length})</h4>
                  <div className="space-y-2">
                    {fedina.note.map((nota, idx) => {
                      const tipoConfig = TIPI_NOTA.find(t => t.value === nota.tipo);
                      return (
                        <div key={idx} className="bg-slate-50 p-3 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <Badge className={tipoConfig?.color || 'bg-slate-100'}>
                                {tipoConfig?.label || nota.tipo}
                              </Badge>
                              <p className="text-sm mt-1">{nota.descrizione}</p>
                              <p className="text-xs text-slate-400">{nota.agente_nome} - {new Date(nota.created_at).toLocaleDateString('it-IT')}</p>
                            </div>
                            <Badge variant="outline">Gravità: {nota.gravita}/5</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Storico Multe */}
              <div>
                <h4 className="font-medium mb-3">Storico Multe ({fedina.storico_multe?.length || 0})</h4>
                {fedina.storico_multe?.length === 0 ? (
                  <p className="text-slate-400 text-center py-4">Nessuna multa nello storico</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {fedina.storico_multe?.map(multa => (
                      <div key={multa.id} className="bg-slate-50 p-3 rounded-lg flex justify-between items-center">
                        <div>
                          <p className="font-medium text-sm">{multa.motivo}</p>
                          <p className="text-xs text-slate-500">{multa.luogo} - {new Date(multa.data_infrazione).toLocaleDateString('it-IT')}</p>
                        </div>
                        <div className="text-right">
                          {getStatoBadge(multa.stato)}
                          <p className="font-medium mt-1">{formatCurrency(multa.importo)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Multe;
